import { useEffect, useState } from "react";
import { useParams, NavLink } from "react-router-dom";
import { loadScript } from "../../../../../globals/constants";
import { jobService } from "../../../../../services/job-service";
import { formatDate } from "../../../admin/components/admin-ui";
import SectionBlogsSidebar from "../../sections/blogs/sidebar/section-blogs-sidebar";

function BlogDetailPage() {
    const { id } = useParams();
    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState("");

    useEffect(() => {
        loadScript("js/custom.js");
    }, []);

    useEffect(() => {
        setLoading(true);
        jobService.blog(id)
            .then((res) => {
                setBlog(res.blog);
                setLoading(false);
                if (res.blog) {
                    // Dynamically set document SEO title & description
                    document.title = res.blog.seo_title || `${res.blog.title} | Zozo Jobs`;
                    const metaDesc = document.querySelector('meta[name="description"]');
                    if (metaDesc) {
                        metaDesc.setAttribute("content", res.blog.seo_description || res.blog.description?.replace(/<[^>]*>/g, "").substring(0, 160));
                    }
                }
            })
            .catch((err) => {
                setErrorMsg(err.message || "Failed to load the article.");
                setLoading(false);
            });
    }, [id]);

    if (loading) {
        return (
            <div className="section-full p-t120 p-b90 bg-white text-center">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted">Loading article...</p>
            </div>
        );
    }

    if (errorMsg || !blog) {
        return (
            <div className="section-full p-t120 p-b90 bg-white text-center">
                <div className="alert alert-danger d-inline-block m-a20">
                    {errorMsg || "Article not found."}
                </div>
                <div>
                    <NavLink to="/blog-list" className="btn btn-primary btn-sm">Back to Blog List</NavLink>
                </div>
            </div>
        );
    }

    return (
        <div className="section-full p-t120 p-b90 bg-white">
            <div className="container">
                <div className="row d-flex justify-content-center">
                    <div className="col-lg-8 col-md-12">
                        <div className="blog-post-single-outer">
                            <div className="blog-post-single bg-white">
                                <div className="wt-post-info">
                                    {/* Featured Image */}
                                    <div className="wt-post-media m-b30" style={{ borderRadius: "8px", overflow: "hidden" }}>
                                        {blog.featured_image_url ? (
                                            <img src={blog.featured_image_url} alt={blog.title} style={{ width: "100%", maxHeight: "450px", objectFit: "cover" }} />
                                        ) : (
                                            <div className="bg-light d-flex align-items-center justify-content-center" style={{ width: "100%", height: "300px" }}>
                                                <i className="fa fa-image fa-5x text-muted" />
                                            </div>
                                        )}
                                    </div>

                                    {/* Meta Header */}
                                    <div className="wt-post-title">
                                        <div className="wt-post-meta-list d-flex gap-3 text-muted mb-2" style={{ fontSize: "14px" }}>
                                            <span><i className="fa fa-calendar-alt me-1 text-primary" />{formatDate(blog.created_at)}</span>
                                            <span><i className="fa fa-user me-1 text-primary" />By {blog.author_name || "Admin"}</span>
                                            {blog.category_name && (
                                                <span className="badge bg-light text-primary border">{blog.category_name}</span>
                                            )}
                                        </div>
                                        <h3 className="post-title fw-bold text-dark mb-4" style={{ fontSize: "28px", lineHeight: "1.3" }}>
                                            {blog.title}
                                        </h3>
                                    </div>

                                    {/* Stats Info */}
                                    <div className="d-flex align-items-center gap-3 border-top border-bottom py-2 mb-4 text-muted" style={{ fontSize: "13px" }}>
                                        <span><i className="fa fa-eye text-primary me-1" /> {blog.views || 0} views</span>
                                        <span>•</span>
                                        <span><i className="fa fa-clock text-primary me-1" /> {blog.read_time ? `${blog.read_time} min read` : "1 min read"}</span>
                                    </div>

                                    {/* Body Description (HTML content) */}
                                    <div 
                                        className="wt-post-discription text-dark" 
                                        style={{ fontSize: "16px", lineHeight: "1.8" }}
                                        dangerouslySetInnerHTML={{ __html: blog.description }} 
                                    />

                                    {/* Tags Section */}
                                    {blog.tags && (
                                        <div className="post-tags-area border-top pt-4 mt-4">
                                            <h5 className="fw-semibold mb-2">Tags:</h5>
                                            <div className="tagcloud d-flex flex-wrap gap-2">
                                                {blog.tags.split(",").map((tag) => {
                                                    const cleanTag = tag.trim();
                                                    if (!cleanTag) return null;
                                                    return (
                                                        <NavLink 
                                                            key={cleanTag} 
                                                            to={`/blog-list?search=${cleanTag}`} 
                                                            className="badge bg-light text-dark border px-3 py-2 text-decoration-none hover-primary"
                                                        >
                                                            #{cleanTag}
                                                        </NavLink>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Share Social Links */}
                            <div className="post-area-tags-wrap mt-4 border-top pt-4">
                                <div className="post-social-icons-wrap">
                                    <h5 className="mb-3 fw-semibold">Share this Article</h5>
                                    <ul className="post-social-icons d-flex gap-3 list-unstyled">
                                        <li><a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`} className="fab fa-facebook-f btn btn-outline-primary" aria-label="Share on Facebook" target="_blank" rel="noreferrer"></a></li>
                                        <li><a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(blog.title)}`} className="fab fa-twitter btn btn-outline-info" aria-label="Share on Twitter" target="_blank" rel="noreferrer"></a></li>
                                        <li><a href={`https://www.linkedin.com/shareArticle?url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(blog.title)}`} className="fab fa-linkedin-in btn btn-outline-primary" aria-label="Share on LinkedIn" target="_blank" rel="noreferrer"></a></li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-4 col-md-12 rightSidebar">
                        <SectionBlogsSidebar />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default BlogDetailPage;